import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { safeHubPath } from '../security/hub-path';
import { useBootstrap, useHub } from './context';
import { readHubState } from './hooks';
import { rememberSigningInProduct } from './signing-in-product';
import { SigningInPage } from './signing-in';
import { SettingsPlaceholder, isSettingsPath } from './settings-placeholder';

/**
 * Keeping the hub signed in across a reload (hosted dev 2026-10-08: "every refresh I get the sign in page").
 *
 * The hub keeps its tokens in memory only (auth-ui Auth0Session, cacheLocation memory: no token in localStorage, a
 * deliberate security choice), so a reload starts with no session even though Auth0 still has one. Instead of showing
 * the signed-out state, a page that a sign-in may come back to (safeHubPath: the organization settings and invitation
 * pages) first asks Auth0 without prompting: a silent sign-in (prompt=none, a top-level redirect, no third-party
 * cookies) whose callback returns to the exact page. Only when Auth0 says a person must act does the page show its own
 * sign-in. The other hub pages (sign-in, callback, product choice) handle sessions themselves.
 *
 * Loop guard: the callback returns from a silent attempt that found no session with silentTried in the router state,
 * which this gate never acts on; and a failed attempt is remembered for RESTORE_WINDOW_MS for that path (sessionStorage,
 * per tab), so a lost router state cannot loop either. A successful sign-in clears the record (clearSilentRestore),
 * so every later reload restores again. Signing out ends the Auth0 session itself, so the next silent attempt fails
 * and the signed-out page shows: no silent re-entry.
 */
const RESTORE_KEY = 'trace.hub.silentRestore';
export const RESTORE_WINDOW_MS = 2 * 60 * 1000;
/**
 * How long the placeholder stays after the silent redirect started before the page shows itself anyway: a redirect
 * leaves within a second, so a tab still here after this one never left (a blocked navigation), and the page's own
 * sign-in is then the answer.
 */
export const REDIRECT_GRACE_MS = 10_000;

interface RestoreRecord {
  path: string;
  at: number;
}

function readRecord(): RestoreRecord | null {
  try {
    const raw = window.sessionStorage.getItem(RESTORE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<RestoreRecord>;
    return typeof value.path === 'string' && typeof value.at === 'number'
      ? { path: value.path, at: value.at }
      : null;
  } catch {
    return null;
  }
}

/**
 * True while this tab is in the middle of a silent restore of an organization settings page (a recent record, not yet
 * cleared by the callback): the callback then shows the settings placeholder instead of the signing-in screen, so a
 * reload of a settings page looks the same from its first paint until the page is back.
 */
export function restoringSettings(now: number = Date.now()): boolean {
  const record = readRecord();
  return record !== null && now - record.at < RESTORE_WINDOW_MS && isSettingsPath(record.path);
}

function recentlyTried(path: string, now: number): boolean {
  const record = readRecord();
  return record !== null && record.path === path && now - record.at < RESTORE_WINDOW_MS;
}

function markTried(path: string, now: number): boolean {
  try {
    window.sessionStorage.setItem(RESTORE_KEY, JSON.stringify({ path, at: now } satisfies RestoreRecord));
    return true;
  } catch {
    // Storage blocked: without the record a lost router state could loop, so no silent attempt is made.
    return false;
  }
}

/** Called once a sign-in completed: the next reload may restore silently again. */
export function clearSilentRestore(): void {
  try {
    window.sessionStorage.removeItem(RESTORE_KEY);
  } catch {
    // Nothing recorded, or storage blocked.
  }
}

/** The page a silent sign-in may come back to (path and the query safeHubPath allows), or undefined. */
export function restorablePath(pathname: string, search: string): string | undefined {
  return safeHubPath(`${pathname}${search}`) ?? safeHubPath(pathname);
}

export function SessionRestoreGate({ children }: { children: ReactNode }) {
  const deps = useHub();
  const { host } = useBootstrap();
  const location = useLocation();
  const auth0 = deps.sessions.auth0;
  const hubPath = restorablePath(location.pathname, location.search);
  const { silentTried } = readHubState(location.state);
  const eligible = auth0 !== null && hubPath !== undefined && !silentTried && deps.sessions.active() === null;
  const [checking, setChecking] = useState(eligible);
  const started = useRef(false);
  const leaveTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Once per page load: StrictMode's second effect run and later navigations inside the hub never start another.
    if (!eligible || !auth0 || !hubPath || started.current) return;
    started.current = true;
    void (async () => {
      const session = await deps.sessions.restore();
      const now = Date.now();
      if (session || recentlyTried(hubPath, now) || !markTried(hubPath, now)) {
        setChecking(false);
        return;
      }
      rememberSigningInProduct(null);
      try {
        // Resolves once the redirect has STARTED (auth0-spa-js assigns window.location and returns), while the page is
        // still on screen for a few hundred milliseconds. Showing the page then rendered its signed-out state, "Sign in
        // to manage your organization", on every reload until the browser left (hosted dev 2026-10-08, found with the
        // recorder's DOM log). So the placeholder stays; the page shows only if the tab is still here long after.
        await auth0.beginSignIn({ hubPath, prompt: 'none' });
        leaveTimer.current = window.setTimeout(() => setChecking(false), REDIRECT_GRACE_MS);
      } catch (error) {
        console.warn('account-hub: silent sign-in did not start', error);
        setChecking(false);
      }
    })();
  }, [eligible, auth0, hubPath, deps]);

  // The grace timer never outlives the gate (unmount in tests, or a navigation inside the hub).
  useEffect(() => () => window.clearTimeout(leaveTimer.current), []);

  // While Trace checks for an existing sign-in: a settings page shows its own placeholder (the same markup as its first
  // paint and its loading), any other page the steady signing-in screen. No sign-in form, no flicker.
  if (eligible && checking) {
    return hubPath !== undefined && isSettingsPath(hubPath) ? (
      <SettingsPlaceholder brand={host.kind === 'trace'} />
    ) : (
      <SigningInPage productKey={null} />
    );
  }
  return <>{children}</>;
}
